import React from 'react'
import { Box, MenuItem, TextField } from '@mui/material'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import { type SisSyncRunOutcome, type SisSyncRunTrigger } from '../../models/SisSyncRun'
import { OUTCOME_LABELS, TRIGGER_LABELS } from '../../helpers/sisSyncRunDisplay'
import { EMPTY_FILTERS, type SisSyncRunFilterValues } from './helpers'

interface SisSyncRunsFiltersProps {
  values: SisSyncRunFilterValues
  onChange: (values: SisSyncRunFilterValues) => void
}

const fieldSx = { width: '11rem' }

export const SisSyncRunsFilters = ({ values, onChange }: SisSyncRunsFiltersProps): JSX.Element => {
  const set = (patch: Partial<SisSyncRunFilterValues>): void => { onChange({ ...values, ...patch }) }
  const hasFilters = Object.values(values).some((value) => value !== '')
  // min/max only steer the picker; a typed date can still land on the wrong side. yyyy-MM-dd compares as a string.
  const isRangeInverted = values.startedFrom !== '' && values.startedTo !== '' && values.startedFrom > values.startedTo

  return (
    <Box display='flex' gap={2} flexWrap='wrap' alignItems='center'>
      <TextField
        select
        size='small'
        label='Status'
        value={values.outcome}
        onChange={(event) => { set({ outcome: event.target.value as SisSyncRunOutcome | '' }) }}
        sx={fieldSx}
      >
        <MenuItem value=''>All</MenuItem>
        {(Object.keys(OUTCOME_LABELS) as SisSyncRunOutcome[]).map((outcome) => (
          <MenuItem key={outcome} value={outcome}>{OUTCOME_LABELS[outcome]}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size='small'
        label='Trigger'
        value={values.trigger}
        onChange={(event) => { set({ trigger: event.target.value as SisSyncRunTrigger | '' }) }}
        sx={fieldSx}
      >
        <MenuItem value=''>All</MenuItem>
        {(Object.keys(TRIGGER_LABELS) as SisSyncRunTrigger[]).map((trigger) => (
          <MenuItem key={trigger} value={trigger}>{TRIGGER_LABELS[trigger]}</MenuItem>
        ))}
      </TextField>
      <TextField
        type='date'
        size='small'
        label='Started from'
        value={values.startedFrom}
        onChange={(event) => { set({ startedFrom: event.target.value }) }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: values.startedTo || undefined }}
        sx={fieldSx}
      />
      <TextField
        type='date'
        size='small'
        label='Started to'
        value={values.startedTo}
        onChange={(event) => { set({ startedTo: event.target.value }) }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: values.startedFrom || undefined }}
        error={isRangeInverted}
        helperText={isRangeInverted ? 'Must be on or after Started from' : undefined}
        sx={fieldSx}
      />
      {hasFilters && (
        <GenericButton
          label='Clear filters'
          buttonType={ButtonType.Ghost}
          buttonSize={ButtonSize.Medium}
          onClick={() => { onChange(EMPTY_FILTERS) }}
        />
      )}
    </Box>
  )
}
